import React, { memo, useCallback } from "react";

import { useIntl } from "react-intl";

import { EntityType, EntityTypeSupportingCard, Person } from "@mapmycustomers/shared/types/entity";

import AddOrCreateDropdown from "@app/component/preview/components/AddOrCreateDropdown";
import Section from "@app/component/preview/components/Section";

import styles from "../../Relationships.module.scss";
import EditPeoplePanel from "../EditPeoplePanel";

interface Props {
  addPersonLoading?: boolean;
  entityType: EntityTypeSupportingCard;
  onAddExisting: () => void;
  onAddNew: () => void;
  onChangePrimaryPerson?: () => void;
  onFetchMorePeople?: () => void;
  onRemove?: (id: Person["id"]) => void;
  onUnsetPrimaryPerson?: () => void;
  people: Person[];
  peopleMultiSelect: boolean;
  primaryPersonId?: Person["id"];
  totalPeople: number;
}

const People: React.FC<Props> = ({
  addPersonLoading = false,
  entityType,
  onAddExisting,
  onAddNew,
  onChangePrimaryPerson,
  onFetchMorePeople,
  onRemove,
  onUnsetPrimaryPerson,
  people,
  peopleMultiSelect,
  primaryPersonId,
  totalPeople,
}) => {
  const intl = useIntl();

  const handleRemovePerson = useCallback((person: Person) => onRemove?.(person.id), [onRemove]);

  return (
    <Section
      action={
        entityType === EntityType.DEAL && people.length > 0 ? null : (
          <AddOrCreateDropdown
            loading={addPersonLoading}
            onAddExisting={onAddExisting}
            onAddNew={onAddNew}
            onChangePrimary={onChangePrimaryPerson}
            onUnsetPrimary={onUnsetPrimaryPerson}
            showPrimary={entityType === EntityType.COMPANY}
          />
        )
      }
      title={
        peopleMultiSelect
          ? intl.formatMessage(
              {
                id: "entityPreview.relationships.people.title",
                defaultMessage: "People ({count})",
                description: "People section title of the preview record pane",
              },
              { count: totalPeople }
            )
          : intl.formatMessage({
              id: "entityPreview.relationships.person.title",
              defaultMessage: "Person",
              description: "Person section title of the preview record pane",
            })
      }
      titleClassName={styles.sectionTitle}
    >
      <div className={styles.container}>
        <EditPeoplePanel
          noDataText={
            entityType === EntityType.COMPANY
              ? intl.formatMessage({
                  id: "recordPane.relationships.people.noData.forCompany",
                  defaultMessage: "Associate a person with this company",
                  description:
                    "No data text in an associated people section in *Edit panes for company",
                })
              : entityType === EntityType.DEAL
              ? intl.formatMessage({
                  id: "recordPane.relationships.people.noData.forDeal",
                  defaultMessage: "Associate a person with this deal",
                  description:
                    "No data text in an associated people section in *Edit panes for deal",
                })
              : ""
          }
          onFetchMorePeople={onFetchMorePeople}
          onRemove={handleRemovePerson}
          people={people}
          primaryPersonId={primaryPersonId}
          totalPeople={totalPeople}
        />
        {people.length !== 1 ? null : (
          // a dummy div to force single person be in a first column
          <div />
        )}
      </div>
    </Section>
  );
};

export default memo(People);
